import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Building2, FolderKanban, Plus, Trash2 } from "lucide-react";
import client, { absoluteUrl } from "../api/client";
import Modal from "../Moodboard/Modal";
import FirmProfileDialog from "./FirmProfileDialog";
import "../Moodboard/Moodboard.css";
import "./Architect.css";

export const STATUSES = [
  { key: "concept", label: "Concept" },
  { key: "approved", label: "Approved" },
  { key: "ordered", label: "Ordered" },
  { key: "completed", label: "Completed" },
];
export const statusLabel = (key) => STATUSES.find((entry) => entry.key === key)?.label || key;

const relative = (value) => {
  if (!value) return "";
  const days = Math.round((Date.now() - new Date(value).getTime()) / 86400000);
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return new Date(value).toLocaleDateString();
};

function NewProjectDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", client_name: "", site: "" });
  const [busy, setBusy] = useState(false);
  const set = (key) => (event) => setForm((current) => ({ ...current, [key]: event.target.value }));

  const create = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      const { data } = await client.post("/api/projects", form);
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not create the project.");
      setBusy(false);
    }
  };

  return (
    <Modal title="New project" onClose={onClose}>
      <form className="ar-form" onSubmit={create}>
        <label className="ar-field">
          <span className="ar-field-label">Project name</span>
          <input type="text" value={form.name} maxLength={120} onChange={set("name")} placeholder="e.g. Mehta Residence" autoFocus required />
        </label>
        <label className="ar-field">
          <span className="ar-field-label">Client</span>
          <input type="text" value={form.client_name} maxLength={120} onChange={set("client_name")} placeholder="e.g. Mr & Mrs Mehta" />
        </label>
        <label className="ar-field">
          <span className="ar-field-label">Site</span>
          <input type="text" value={form.site} maxLength={200} onChange={set("site")} placeholder="e.g. Koregaon Park, Pune" />
        </label>
        <div className="mb-modal-actions">
          <button type="button" className="mb-text-button" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="mb-primary" disabled={busy || !form.name.trim()}>
            {busy ? "Creating..." : "Create project"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default function ProjectList() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await client.get("/api/projects");
      setProjects(data.items);
      setError("");
    } catch {
      setError("Could not load your projects.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (project) => {
    if (!window.confirm(`Delete "${project.name}"? Its mood boards and walls are kept; only the project and its product list go.`)) return;
    try {
      await client.delete(`/api/projects/${project.id}`);
      setProjects((current) => current.filter((entry) => entry.id !== project.id));
    } catch {
      toast.error("Could not delete the project.");
    }
  };

  const shown = filter === "all" ? projects : projects.filter((project) => project.status === filter);
  const count = (key) => projects.filter((project) => project.status === key).length;

  return (
    <main className="mb-page ar-page">
      <ToastContainer position="bottom-right" />
      <header className="mb-page-head">
        <div>
          <p className="mb-kicker">Studio</p>
          <h1>Projects</h1>
        </div>
        <div className="ar-head-actions">
          <button type="button" className="mb-tool" onClick={() => setDialog("firm")}>
            <Building2 size={16} strokeWidth={1.8} aria-hidden="true" />
            Firm details
          </button>
          <button type="button" className="mb-primary" onClick={() => setDialog("new")}>
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            New project
          </button>
        </div>
      </header>

      {error && <p className="mb-error">{error}</p>}

      {loading ? (
        <p className="mb-muted">Loading projects...</p>
      ) : !projects.length && !error ? (
        <section className="mb-empty">
          <h2>Keep each client's work together</h2>
          <p>
            A project holds the products for each room, the mood boards and wall visualisations you made for it, and turns
            them into a quotation or spec sheet with your firm's letterhead.
          </p>
          <button type="button" className="mb-primary" onClick={() => setDialog("new")}>
            <Plus size={16} strokeWidth={2} aria-hidden="true" />
            New project
          </button>
        </section>
      ) : (
        <>
          <div className="ar-tabs" role="tablist" aria-label="Filter by status">
            {[{ key: "all", label: "All" }, ...STATUSES].map((entry) => (
              <button
                key={entry.key}
                type="button"
                role="tab"
                aria-selected={filter === entry.key}
                className={filter === entry.key ? "active" : ""}
                onClick={() => setFilter(entry.key)}
              >
                {entry.label}
                <span>{entry.key === "all" ? projects.length : count(entry.key)}</span>
              </button>
            ))}
          </div>
          {!shown.length ? (
            <p className="mb-muted">No {statusLabel(filter).toLowerCase()} projects.</p>
          ) : (
            <section className="ar-project-grid">
              {shown.map((project) => (
                <article key={project.id} className="ar-project-card">
                  <Link to={`/projects/${project.id}`} className="ar-project-cover" aria-label={`Open ${project.name}`}>
                    {project.cover ? (
                      <img src={absoluteUrl(project.cover) || undefined} alt="" loading="lazy" />
                    ) : (
                      <FolderKanban size={30} strokeWidth={1.4} aria-hidden="true" />
                    )}
                    <span className={`ar-status is-${project.status}`}>{statusLabel(project.status)}</span>
                  </Link>
                  <div className="ar-project-meta">
                    <div>
                      <h2>
                        <Link to={`/projects/${project.id}`}>{project.name}</Link>
                      </h2>
                      <p>{[project.client_name, project.site].filter(Boolean).join(" · ") || "No client yet"}</p>
                      <p className="ar-muted ar-small">
                        {project.item_count} product{project.item_count === 1 ? "" : "s"} · {project.board_count} board
                        {project.board_count === 1 ? "" : "s"} · {project.visualisation_count} wall
                        {project.visualisation_count === 1 ? "" : "s"} · edited {relative(project.updated_at)}
                      </p>
                    </div>
                    <button
                      type="button"
                      className="mb-icon-button danger"
                      onClick={() => remove(project)}
                      title="Delete project"
                      aria-label={`Delete ${project.name}`}
                    >
                      <Trash2 size={15} strokeWidth={1.8} />
                    </button>
                  </div>
                </article>
              ))}
            </section>
          )}
        </>
      )}

      {dialog === "new" && (
        <NewProjectDialog onClose={() => setDialog(null)} onCreated={(project) => navigate(`/projects/${project.id}`)} />
      )}
      {dialog === "firm" && <FirmProfileDialog onClose={() => setDialog(null)} />}
    </main>
  );
}
